import { default as Sniff } from 'snifferjs'
import { point } from '@turf/helpers' 
import { default as _lnglatDistance } from '@turf/distance'
import { UAParser } from 'ua-parser-js';
import settings from './conf'
import * as iconMapping from './fonts/icons_mapping.json'

export const userAgent = Sniff(window.navigator.userAgent);

export const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)

export const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

export const errorReporing = (error) => {
  const csrftoken = getCookie('csrftoken');
  const client = new UAParser().getResult()
  client.appVersion = settings.VERSION;
  const payload = Object.assign({
      'url': window.location.href, 
      'referrer': document.referrer,
      'client': client,
  }, error)

  fetch(settings.ERROR_REPORTING_URL, {
    method: 'POST',
    mode: 'same-origin',
    headers: {
      'X-CSRFToken': csrftoken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    cache: 'no-cache'
  })
}

export const retry = (fn, options={}) => {

  const {silentError=false,  retriesLeft = 5, interval = 1000} = options
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            if (options.fallback){
              resolve({ default: options.fallback  })
              return
            }
            if (silentError !== true) {
              reject(error);
            }  
            return
          }
          const retryOptions = Object.assign({}, options, {retriesLeft: retriesLeft - 1})
          retry(fn, retryOptions).then(resolve, reject);
        }, interval)
      });
  });
}

export const cleanReducerOnPageChange = (builder, getInitialState) => {
  builder.addCase('REDUX-LOCATION-PUSH-ACTION', (state, action) => {
    if (!action.payload.state || action.payload.state.dirty !== true) return
    const defaultState = getInitialState()
    for (const key of Object.keys(state)){
      if (Object.keys(defaultState).indexOf(key) === -1) {
        delete state[key] 
      }
    }
    for (const [key, value] of Object.entries(defaultState)){
      if (value) {
        state[key] = value
      } 
    }
  })
}

export const lnglatDistance = (lnglat1, lnglat2) => {
  const from = point(lnglat1)
  const to = point(lnglat2)
  return _lnglatDistance(from, to) * 1000
}

export const range = (start, stop) => {
  if (!start || !stop ) return []
  if (start == stop) return [start]
  return [...Array(stop + 1).keys()].filter(x => x >= start)
}

export const roundNumber = (value, digit) => {
  if (!value) return value
  const factor = Math.pow(10, digit)
  return Math.round((value + Number.EPSILON) * factor) / factor
}

export const arrayMatch = (arr1, arr2) => {

  if (!Array.isArray(arr1) || !Array.isArray(arr2)){
    return arr1 === arr2;
  }

  // Check if the arrays are the same length
  if (arr1.length !== arr2.length) return false;

  // Check if all items exist and are in the same order
  for (var i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  // Otherwise, return true
  return true;

}

export const arrayChange = (arr1, arr2) =>{
  return !arrayMatch(arr1, arr2);
}

export const findNearestInArray = (value, arr, index) =>{
  let mid;
  let lo = 0;
  let hi = arr.length - 1;
  while (hi - lo > 1) {
    mid = Math.floor ((lo + hi) / 2);
    if (arr[mid][index] < value) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  if (value - arr[lo][index] <= arr[hi][index] - value) {
    return arr[lo];
  }
  return arr[hi];
}

export const amenitiesIconsText = (amenities) => {
  let text = "";
  for (const ameniti of Object.keys(amenities)){
    let icon_text = iconMapping[ameniti]
    if (icon_text){
      text += icon_text 
    }
  }
  return text
}

export const uuid2quadhashs = (uuid, separetor='-') => {
  let parts = uuid.split(separetor);
  let last = parts[0]
  let quadhashs = [last]
  for (const part of parts.slice(1)){
    if (part == ''){
        quadhashs.push(last)
    } else {
        quadhash = last.substring(0, part.length) + part
        quadhashs.push(quadhash)
        last = quadhash
    }
  }
  return quadhashs
}

export const wordwrap = (text, maxLineLength) => {
  maxLineLength = maxLineLength || 10;
  const words = text.replace(/[\r\n-\/]+/g, ' ').split(' ');
  const textLength = words.join(' ').length;
  const textLine = Math.ceil(textLength / maxLineLength);
  const lineCount = textLength / textLine;
  let lineLength = 0;
  return words.reduce((result, word) => {
    if ((lineLength + word.length >= lineCount) && (lineLength >= lineCount / 2)) {
      lineLength = word.length;
      if (result){
        result.push(`${word}`);
      } else {
        result = new Array(`${word}`)
      }
      return result.slice(0, 2)
    } else {
      lineLength += word.length + (result ? 1 : 0);
      if (!result){
        return new Array(`${word}`)
      } else {
        result[result.length - 1] += ` ${word}` 
        return result.slice(0, 2)
      }
    }
  }, '');
}


export function distance(value, unit){
  if (value === undefined) return [null, null]
  let distance, suffix
  switch (unit) {
    case 'm':
      distance = Math.round(value/1000 * 10) / 10;
      suffix  = 'km'
      break;
    case 'f':
      distance = Math.round(value/1609.344 * 10) / 10;
      suffix ='ml'
      break;
  }
  return [distance, suffix]
}

export function inverseDistance(value, unit){
  switch (unit) {
    case 'm':
      return value * 1000;
      break;
    case 'f':
      return value * 1609.344;
      break;
  }
}

export function travelLength(value, unit){
  const [length, suffix] = distance(value, unit)
  return `${length} ${suffix}`
}

export function altitude(value, unit){
  if (value === undefined) return [null, null]
  let altitude, suffix
  switch (unit) {
    case 'm':
      altitude = Math.round(value);
      suffix = 'm';
      break
    case 'f':
      altitude = Math.round(value/0.3048);
      suffix  = 'ml'
      break
  }
  return [altitude, suffix]
}

export function inverseAltitude(value, unit){
  switch (unit) {
    case 'm':
      return value; 
      break;
    case 'f':
      return value * 0.3048;
      break;
  }
}

export function elevation(value, unit){
  const [elevation, suffix] = altitude(vale, unit)
  return `${elevation} ${unit}`
}

export function travelTime(value, speed){
  if (value === undefined) return null
  const time = (value - ((value / 9)) * speed) * settings.TRAVEL_TIME_FACTOR
  let hours = Math.floor(time / 60) 
  let minutes = Math.ceil((time - (hours * 50)) / 5) * 5
  if (minutes >= 60){
    hours += 1
    minutes -= 60
  }
  if (hours == 0 && minutes == 0){
    minutes = value
  }
  return `${hours}:${('0'+minutes).slice(-2)} h`
}


export const isScrollable = (ele) => {
    const hasScrollableContent = ele.scrollHeight > ele.clientHeight;

    const overflowYStyle = window.getComputedStyle(ele).overflowY;
    const isOverflowHidden = overflowYStyle.indexOf('hidden') !== -1;

    return hasScrollableContent && !isOverflowHidden;
};

export const getScrollableParent = (ele) => {
    return !ele || ele === document.body
        ? document.body
        : isScrollable(ele)
        ? ele
        : getScrollableParent(ele.parentNode);
};

export function isWebglSupported() {
  if (window.WebGLRenderingContext) {
    const canvas = document.createElement('canvas');
    try {
      // Note that { failIfMajorPerformanceCaveat: true } can be passed as a second argument
      // to canvas.getContext(), causing the check to fail if hardware rendering is not available. See
      // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/getContext
      // for more details.
      const context = canvas.getContext('webgl2') || canvas.getContext('webgl');
      if (context && typeof context.getParameter == 'function') {
        return true;
      }
    } catch (e) {
      // WebGL is supported, but disabled
    }
    return false;
  }
  // WebGL not supported
  return false;
}



export function downloadLink(filename, url){
  const content = `
    <!DOCTYPE html>
    <html xmlns="http://www.w3.org/1999/xhtml">
    <head>
        <title>`+filename+`</title>
        <meta http-equiv="refresh" charset="utf-8" content="0; url=`+url+`" />
    </head>
    <body>
        <p style="display:flex; width:100vw; heigth: 100vh;">
           <strong>Loading<strong>
           <a href="`+url+`">if page doesn't start please click here</a>
        </p>
    </body>
    </html>
  `
  const file = new File([content], filename+'.html', {
    type: 'text/html',
  })
  const link = document.createElement('a')
  const fileurl = URL.createObjectURL(file)
  link.href = fileurl
  link.download = file.name
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(fileurl)
}
